import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner, ConfirmModal, RowActions, RowAction, btnPrimary } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import {
  fetchFileBlob,
  formatFileSize,
  PMFile,
} from '../services/fileApi';
import { usePMFileStore } from '../stores/pmFileStore';
import FileUploadDialog from './FileUploadDialog';
import FileTypeIcon from './FileTypeIcon';
import EmptyPanel from './EmptyPanel';

interface FileListProps {
  projectId: string;
}

/**
 * 需認證的圖片縮圖：以帶認證的請求取得 blob 後顯示（img 標籤無法帶 Authorization header）
 */
const AuthImage: React.FC<{ file: PMFile }> = ({ file }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchFileBlob(file.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  if (failed) return <FileTypeIcon mimeType={file.mimeType} />;
  if (!src) return <span className="text-[13px] text-fg-muted">…</span>;
  return <img src={src} alt={file.fileName} className="w-full h-full object-cover" />;
};

export interface FileListRef {
  openUploadDialog: () => void;
}

const FileList = React.forwardRef<FileListRef, FileListProps>(({ projectId }, ref) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<PMFile | null>(null);

  // 使用 store
  const {
    files,
    isLoading,
    deletingFileId,
    fetchFiles,
    deleteFile,
  } = usePMFileStore();

  // 每次進入都背景重抓（有快取先顯示），別人上傳的檔案才看得到
  useEffect(() => {
    fetchFiles(projectId);
  }, [projectId, fetchFiles]);

  // 預覽關閉時釋放 blob URL
  useEffect(() => {
    return () => {
      if (previewImage) URL.revokeObjectURL(previewImage);
    };
  }, [previewImage]);

  const handleDownload = async (file: PMFile) => {
    try {
      const blob = await fetchFileBlob(file.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下載檔案失敗:', error);
      toast.error(t('common.messages.operationFailed'));
    }
  };

  const handlePreview = async (file: PMFile) => {
    // 只有圖片類型才能預覽
    if (!file.mimeType.startsWith('image/')) return;
    try {
      const blob = await fetchFileBlob(file.id);
      setPreviewImage(URL.createObjectURL(blob));
    } catch (error) {
      console.error('預覽檔案失敗:', error);
      toast.error(t('common.messages.operationFailed'));
    }
  };

  const isImageFile = (mimeType: string) => {
    return mimeType.startsWith('image/');
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;
    const file = fileToDelete;
    setFileToDelete(null);

    try {
      await deleteFile(file.id);
    } catch (error: any) {
      console.error('刪除檔案失敗:', error);
    }
  };

  // 暴露方法給父組件（必須在任何條件 return 之前）
  React.useImperativeHandle(ref, () => ({
    openUploadDialog: () => setIsUploadDialogOpen(true),
  }));

  if (isLoading && files.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* 檔案列表：一個容器＋細線分隔（R13） */}
      {files.length === 0 ? (
        <EmptyPanel
          title={t('pages:pm.files.noFiles')}
          hint={t('pages:pm.files.emptyHint')}
          action={
            <button type="button" onClick={() => setIsUploadDialogOpen(true)} className={btnPrimary}>
              {t('pages:pm.files.uploadFile')}
            </button>
          }
        />
      ) : (
        <ul className="bg-surface rounded-lg border border-line divide-y divide-line overflow-hidden">
          {files.map((file) => (
            <li key={file.id} className="px-5 py-3 flex items-center gap-3">
              {/* 檔案圖示或圖片縮圖 */}
              {isImageFile(file.mimeType) ? (
                <button
                  type="button"
                  className="w-10 h-10 shrink-0 rounded overflow-hidden flex items-center justify-center bg-slate-100 dark:bg-slate-700"
                  onClick={() => handlePreview(file)}
                  aria-label={file.fileName}
                >
                  <AuthImage file={file} />
                </button>
              ) : (
                <div className="w-10 h-10 shrink-0 flex items-center justify-center">
                  <FileTypeIcon mimeType={file.mimeType} />
                </div>
              )}

              {/* 檔案資訊 */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-fg truncate">{file.fileName}</p>
                <p className="mt-0.5 text-[13px] text-fg-secondary tabular-nums truncate">
                  {formatFileSize(file.fileSize)}
                  <span className="mx-1.5 text-fg-muted">·</span>
                  {formatTableDate(file.createdAt, i18n.language)}
                  {file.storageType === 'SUPABASE' && (
                    <>
                      <span className="mx-1.5 text-fg-muted">·</span>
                      {t('pages:pm.files.cloudStorage')}
                    </>
                  )}
                  {file.description && (
                    <>
                      <span className="mx-1.5 text-fg-muted">·</span>
                      {file.description}
                    </>
                  )}
                </p>
              </div>

              {/* 操作 */}
              <RowActions>
                <RowAction onClick={() => handleDownload(file)}>{t('common.actions.download')}</RowAction>
                {deletingFileId === file.id ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <RowAction tone="danger" onClick={() => setFileToDelete(file)}>{t('common.actions.delete')}</RowAction>
                )}
              </RowActions>
            </li>
          ))}
        </ul>
      )}

      {/* 上傳對話框 */}
      <FileUploadDialog
        isOpen={isUploadDialogOpen}
        onClose={(shouldRefresh) => {
          setIsUploadDialogOpen(false);
          if (shouldRefresh) {
            fetchFiles(projectId);
          }
        }}
        projectId={projectId}
      />

      <ConfirmModal
        isOpen={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDelete}
        title={t('common.actions.delete')}
        message={t('pages:pm.files.confirmDelete', { fileName: fileToDelete?.fileName })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        t={t}
      />

      {/* 圖片預覽 */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 animate-overlay-in motion-reduce:animate-none"
          onMouseDown={() => setPreviewImage(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            aria-label={t('common.actions.close')}
          >
            <XMarkIcon className="h-7 w-7" />
          </button>
          <img
            src={previewImage}
            alt=""
            className="max-w-full max-h-full object-contain rounded"
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
});

FileList.displayName = 'FileList';

export default FileList;
